// Pure helpers for the org-wide bot working-hours settings (Settings → Bot working hours;
// GET/PUT /api/settings/bot-service-hours). The value says when the clinic chatbots treat an
// inbound patient message as OUT OF SERVICE HOURS and what happens then — reply with a message
// or stay silent — with per-WhatsApp-number and in-app-channel overrides (null = inherit the
// defaults), date exceptions ("special days") and time-bounded emergency overrides on top.
// Mirrors the server's validation (healo_backend internal/botservicehours) so a bad save is
// refused here with the reason inline. Framework-free so vitest covers every rule.

/** Canonical Monday-first weekday keys of a weekly map. */
export const WEEKDAY_KEYS = Object.freeze(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

/** Display labels for the weekday keys. */
export const WEEKDAY_LABELS = Object.freeze({
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
});

/** What happens to a message that arrives while closed: reply with the message, or stay silent. */
export const MODES = Object.freeze(["reply", "silent"]);

/** Server cap on an out-of-service message (runes). */
export const MESSAGE_MAX = 4000;
/** Server cap on a special-day / emergency label (runes; required, min 1). */
export const LABEL_MAX = 120;
/** Server cap on windows per day. */
export const MAX_WINDOWS_PER_DAY = 2;

export const MAX_BYPASS_PHONES = 20;

export const BYPASS_PHONE_MIN_DIGITS = 7;

export const BYPASS_PHONE_MAX_DIGITS = 15;
/** Server cap on a bounded emergency's span, in days (until = null is open-ended). */
export const EMERGENCY_MAX_SPAN_DAYS = 14;

/** A fresh all-closed weekly map (every day present, every list empty). */
export function emptyWeekly() {
  const out = {};
  for (const key of WEEKDAY_KEYS) out[key] = [];
  return out;
}

/**
 * "HH:MM" (or "H:MM") → minutes since midnight, -1 when malformed. Mirrors the server: a
 * single-digit hour is accepted ("8:30"), minutes are always two digits, no negatives, no
 * trailing garbage, hours ≤ 23 ("24:00" is invalid).
 */
export function minutesFromHHMM(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? "").trim());
  if (!match) return -1;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

/** Re-render a parsable clock time as zero-padded "HH:MM"; unparsable input returns trimmed. */
export function canonicalHHMM(value) {
  const total = minutesFromHHMM(value);
  if (total < 0) return String(value ?? "").trim();
  const hours = String(Math.floor(total / 60)).padStart(2, "0");
  const minutes = String(total % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** Rune count, matching the server's utf8.RuneCountInString. */
function runeLength(value) {
  return Array.from(String(value ?? "")).length;
}

/**
 * Validate one day's window list against the server's rules: strict HH:MM, start < end (no
 * overnight), at most MAX_WINDOWS_PER_DAY, non-overlapping once sorted (touching allowed —
 * 13:00 end against a 13:00 start is fine). Returns the error message, or "" when valid
 * (an empty list is a valid "closed" day).
 */
export function validateWindows(list) {
  const windows = Array.isArray(list) ? list : [];
  if (windows.length > MAX_WINDOWS_PER_DAY) {
    return `At most ${MAX_WINDOWS_PER_DAY} time windows per day.`;
  }
  const spans = [];
  for (const window of windows) {
    const start = minutesFromHHMM(window?.start);
    const end = minutesFromHHMM(window?.end);
    if (start < 0 || end < 0) return "Enter both times as HH:MM.";
    if (end <= start) return "Each window must end after it starts.";
    spans.push([start, end]);
  }
  spans.sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < spans.length; i++) {
    if (spans[i][0] < spans[i - 1][1]) return "The windows overlap.";
  }
  return "";
}

/** True when `zone` is a usable IANA identifier on this runtime ("" is not a zone). */
export function isValidTimeZoneName(zone) {
  const value = typeof zone === "string" ? zone.trim() : "";
  if (!value) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

// ---- lenient normalization (shape-fill for rendering; nothing is corrected) -----------------

function normalizeWindowList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((window) => ({
    start: String(window?.start ?? "").trim(),
    end: String(window?.end ?? "").trim(),
  }));
}

/** A full 7-key weekly map: unknown keys dropped, missing days closed, windows shape-filled. */
function normalizeWeeklyMap(raw) {
  const source = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const out = {};
  for (const key of WEEKDAY_KEYS) out[key] = normalizeWindowList(source[key]);
  return out;
}

/** Deep-fresh copy of a weekly map (all 7 keys, new window objects). */
export function cloneWeekly(weekly) {
  return normalizeWeeklyMap(weekly);
}

/** Null-or-string gateway-botId scope list: null = every number (and the app channel). */
function normalizeGatewayScope(raw) {
  if (raw == null) return null;
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const id of raw) {
    const value = String(id ?? "").trim();
    if (value && !out.includes(value)) out.push(value);
  }
  return out;
}

/** The nullable override shape shared by the app channel and per-number rows. */
function normalizeOverride(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    enabled: typeof src.enabled === "boolean" ? src.enabled : null,
    timezone:
      typeof src.timezone === "string" && src.timezone.trim() ? src.timezone.trim() : null,
    weekly: src.weekly == null ? null : normalizeWeeklyMap(src.weekly),
    closeOnPublicHolidays:
      typeof src.closeOnPublicHolidays === "boolean" ? src.closeOnPublicHolidays : null,
    mode: typeof src.mode === "string" && src.mode.trim() ? src.mode.trim().toLowerCase() : null,
    // A blank override message means "inherit the default message", so "" reads as null.
    message: typeof src.message === "string" && src.message !== "" ? src.message : null,
  };
}

function normalizeSpecialDay(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const closed = Boolean(src.closed);
  return {
    id: String(src.id ?? "").trim(),
    label: String(src.label ?? ""),
    gatewayBotIds: normalizeGatewayScope(src.gatewayBotIds),
    date: String(src.date ?? "").trim(),
    closed,
    // A closed day carries no windows — the server rejects the combination outright.
    windows: closed ? [] : normalizeWindowList(src.windows),
    message: typeof src.message === "string" && src.message !== "" ? src.message : null,
  };
}

function normalizeEmergency(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    id: String(src.id ?? "").trim(),
    label: String(src.label ?? ""),
    gatewayBotIds: normalizeGatewayScope(src.gatewayBotIds),
    from: String(src.from ?? "").trim(),
    // null = open-ended ("until further notice").
    until: src.until ? String(src.until).trim() : null,
    mode: String(src.mode ?? "").trim().toLowerCase(),
    message: String(src.message ?? ""),
    // Server-stamped; sent back as loaded (the server re-derives them by id anyway). null —
    // never "" — when absent: Go's time.Time unmarshals null as a no-op but rejects "".
    createdBy: String(src.createdBy ?? "").trim(),
    createdAt: src.createdAt ? String(src.createdAt) : null,
  };
}

/**
 * Lenient shape-fill of a raw settings value for rendering and editing: every known field
 * present in canonical shape, invalid strings kept as-is so an editor can show the error.
 * Unknown top-level keys are passed through untouched (the PUT carries them back verbatim).
 * All known fields come back as fresh structures, so this doubles as the draft cloner.
 */
export function normalizeBotServiceHours(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const defaults = src.defaults && typeof src.defaults === "object" ? src.defaults : {};
  const numbers = {};
  const rawNumbers =
    src.numbers && typeof src.numbers === "object" && !Array.isArray(src.numbers)
      ? src.numbers
      : {};
  for (const [rawId, value] of Object.entries(rawNumbers)) {
    // Keys are free-form gateway bot ids — the catalog may lag, so unknown keys are kept.
    const botId = String(rawId ?? "").trim();
    if (botId) numbers[botId] = normalizeOverride(value);
  }
  return {
    ...src, // unknown top-level keys ride along
    version: Number.isFinite(src.version) ? src.version : 1,
    enabled: typeof src.enabled === "boolean" ? src.enabled : true,
    defaults: {
      timezone: String(defaults.timezone ?? "").trim(),
      weekly: normalizeWeeklyMap(defaults.weekly),
      closeOnPublicHolidays: Boolean(defaults.closeOnPublicHolidays),
      holidayCountry: String(defaults.holidayCountry ?? "").trim().toUpperCase(),
      mode: String(defaults.mode ?? "reply").trim().toLowerCase() || "reply",
      message: String(defaults.message ?? ""),
    },
    appChannel: normalizeOverride(src.appChannel),
    numbers,
    specialDays: (Array.isArray(src.specialDays) ? src.specialDays : []).map(normalizeSpecialDay),
    emergencies: (Array.isArray(src.emergencies) ? src.emergencies : []).map(normalizeEmergency),
    bypassPhones: (Array.isArray(src.bypassPhones) ? src.bypassPhones : [])
      .map((phone) => String(phone ?? "").trim())
      .filter(Boolean),
  };
}

// ---- bypass numbers -------------------------------------------------------------------------

/**
 * Mirror of the server's bypass-phone normalization (botservicehours normalizePhone): trim,
 * drop one leading "+", tolerate space/dash/parentheses separators, reject anything else, and
 * require 7–15 digits. Matching is by digit SUFFIX server-side, so the stored value is the
 * bare digit string. Returns { digits, error } — exactly one of the two is set.
 */
export function normalizeBypassPhone(raw) {
  let text = String(raw ?? "").trim();
  if (text.startsWith("+")) text = text.slice(1);
  let digits = "";
  for (const ch of text) {
    if (ch >= "0" && ch <= "9") {
      digits += ch;
    } else if (ch !== " " && ch !== "-" && ch !== "(" && ch !== ")") {
      return { digits: null, error: "Only digits (plus spaces, dashes or parentheses) are allowed." };
    }
  }
  if (digits.length < BYPASS_PHONE_MIN_DIGITS || digits.length > BYPASS_PHONE_MAX_DIGITS) {
    return {
      digits: null,
      error: `A number must be ${BYPASS_PHONE_MIN_DIGITS}–${BYPASS_PHONE_MAX_DIGITS} digits.`,
    };
  }
  return { digits, error: null };
}

// ---- placeholders ---------------------------------------------------------------------------

/**
 * The {{token}} occurrences in `message` that are not in the `allowed` list, in order of first
 * appearance. Matching mirrors the render engine: only the exact name resolves, so
 * "{{ clinicName }}" (with spaces) is unknown. Returned tokens are the raw inner text.
 */
export function findUnknownPlaceholders(message, allowed) {
  const allowedSet = new Set(
    (Array.isArray(allowed) ? allowed : []).map((name) => String(name ?? "")),
  );
  const out = [];
  const pattern = /\{\{([^{}]*)\}\}/g;
  const text = String(message ?? "");
  let match;
  while ((match = pattern.exec(text)) !== null) {
    const inner = match[1];
    if (allowedSet.has(inner)) continue;
    if (!out.includes(inner)) out.push(inner);
  }
  return out;
}

// ---- validation (the server's rules, mirrored) ----------------------------------------------

function isValidDateString(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  // An out-of-range civil date ("2026-02-31") parses to NaN under strict ISO parsing.
  return Number.isFinite(Date.parse(`${value}T00:00:00Z`));
}

/**
 * Mirror of the server's strict write-path validation so Save is refused before the
 * round-trip. → { ok, errors } with path-style keys ("defaults.weekly.mon",
 * "numbers.<botId>.timezone", "specialDays.<id>.date", "emergencies.<id>.range", …).
 * `placeholders` is the server's token vocabulary. Number keys unknown to the gateway
 * catalog are fine — the catalog can lag behind the stored settings.
 */
export function validateBotServiceHours(value, { placeholders = [] } = {}) {
  const settings = normalizeBotServiceHours(value);
  const errors = {};

  const checkMessage = (key, text, { required = false } = {}) => {
    const message = String(text ?? "");
    if (required && !message.trim()) {
      errors[key] = "A message is required in reply mode.";
      return;
    }
    if (runeLength(message) > MESSAGE_MAX) {
      errors[key] = `The message must be at most ${MESSAGE_MAX} characters.`;
      return;
    }
    const unknown = findUnknownPlaceholders(message, placeholders);
    if (unknown.length > 0) {
      const names = unknown.map((token) => `{{${token}}}`).join(", ");
      errors[key] = `Unknown placeholder ${names} — only the listed placeholders are filled in.`;
    }
  };

  const checkLabel = (key, text) => {
    const length = runeLength(String(text ?? "").trim());
    if (length < 1 || length > LABEL_MAX) {
      errors[key] = `The label must be 1–${LABEL_MAX} characters.`;
    }
  };

  const checkScope = (key, gatewayBotIds) => {
    if (Array.isArray(gatewayBotIds) && gatewayBotIds.length === 0) {
      errors[key] = "Select at least one number, or apply it to all numbers.";
    }
  };

  const checkWeekly = (prefix, weekly) => {
    for (const day of WEEKDAY_KEYS) {
      const error = validateWindows(weekly[day]);
      if (error) errors[`${prefix}.${day}`] = error;
    }
  };

  const checkOverride = (prefix, override) => {
    if (override.timezone !== null && !isValidTimeZoneName(override.timezone)) {
      errors[`${prefix}.timezone`] = "Pick a valid timezone.";
    }
    if (override.weekly !== null) checkWeekly(`${prefix}.weekly`, override.weekly);
    if (override.mode !== null && !MODES.includes(override.mode)) {
      errors[`${prefix}.mode`] = 'The mode must be "reply" or "silent".';
    }
    if (override.message !== null) checkMessage(`${prefix}.message`, override.message);
  };

  // Defaults — every field required and valid.
  if (!isValidTimeZoneName(settings.defaults.timezone)) {
    errors["defaults.timezone"] = "Pick a valid timezone.";
  }
  checkWeekly("defaults.weekly", settings.defaults.weekly);
  if (!MODES.includes(settings.defaults.mode)) {
    errors["defaults.mode"] = 'The mode must be "reply" or "silent".';
  }
  checkMessage("defaults.message", settings.defaults.message, {
    required: settings.defaults.mode === "reply",
  });
  if (
    settings.defaults.closeOnPublicHolidays &&
    !/^[A-Z]{2}$/.test(settings.defaults.holidayCountry)
  ) {
    errors["defaults.holidayCountry"] = "Enter a 2-letter country code (e.g. SG).";
  }

  checkOverride("appChannel", settings.appChannel);
  for (const [botId, override] of Object.entries(settings.numbers)) {
    checkOverride(`numbers.${botId}`, override);
  }

  settings.specialDays.forEach((day, index) => {
    const prefix = `specialDays.${day.id || index}`;
    checkLabel(`${prefix}.label`, day.label);
    if (!isValidDateString(day.date)) {
      errors[`${prefix}.date`] = "Enter the date as YYYY-MM-DD.";
    }
    checkScope(`${prefix}.scope`, day.gatewayBotIds);
    if (!day.closed) {
      const windowsError =
        day.windows.length === 0
          ? "An open special day needs at least one time window."
          : validateWindows(day.windows);
      if (windowsError) errors[`${prefix}.windows`] = windowsError;
    }
    if (day.message !== null) checkMessage(`${prefix}.message`, day.message);
  });

  settings.emergencies.forEach((emergency, index) => {
    const prefix = `emergencies.${emergency.id || index}`;
    checkLabel(`${prefix}.label`, emergency.label);
    checkScope(`${prefix}.scope`, emergency.gatewayBotIds);
    if (!MODES.includes(emergency.mode)) {
      errors[`${prefix}.mode`] = 'The mode must be "reply" or "silent".';
    }
    checkMessage(`${prefix}.message`, emergency.message, {
      required: emergency.mode === "reply",
    });
    const from = Date.parse(emergency.from);
    if (!Number.isFinite(from)) {
      errors[`${prefix}.range`] = "Enter a valid start time.";
    } else if (emergency.until !== null) {
      const until = Date.parse(emergency.until);
      if (!Number.isFinite(until)) {
        errors[`${prefix}.range`] = "Enter a valid end time, or leave it open-ended.";
      } else if (until <= from) {
        errors[`${prefix}.range`] = "The override must end after it starts.";
      } else if (until - from > EMERGENCY_MAX_SPAN_DAYS * 24 * 60 * 60 * 1000) {
        errors[`${prefix}.range`] =
          `At most ${EMERGENCY_MAX_SPAN_DAYS} days — leave the end empty for open-ended.`;
      }
    }
  });

  // Bypass numbers — the server 400s the whole save on a bad entry, so block here.
  if (settings.bypassPhones.length > MAX_BYPASS_PHONES) {
    errors["bypassPhones"] = `At most ${MAX_BYPASS_PHONES} bypass numbers.`;
  } else {
    const seen = new Set();
    for (const phone of settings.bypassPhones) {
      const { digits, error } = normalizeBypassPhone(phone);
      if (error) {
        errors["bypassPhones"] = `"${phone}": ${error}`;
        break;
      }
      if (seen.has(digits)) {
        errors["bypassPhones"] = `"${phone}" is listed twice.`;
        break;
      }
      seen.add(digits);
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

// ---- equality (the editor's dirty check) ----------------------------------------------------

function sortedListEqual(a, b) {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
}

function listEqual(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function windowsListEqual(a, b) {
  return (
    a.length === b.length &&
    a.every((window, index) => window.start === b[index].start && window.end === b[index].end)
  );
}

function weeklyEqual(a, b) {
  if (a === null || b === null) return a === b;
  return WEEKDAY_KEYS.every((key) => windowsListEqual(a[key], b[key]));
}

function scopeEqual(a, b) {
  if (a === null || b === null) return a === b;
  return sortedListEqual(a, b);
}

function overrideEqual(a, b) {
  return (
    a.enabled === b.enabled &&
    a.timezone === b.timezone &&
    weeklyEqual(a.weekly, b.weekly) &&
    a.closeOnPublicHolidays === b.closeOnPublicHolidays &&
    a.mode === b.mode &&
    a.message === b.message
  );
}

function numbersEqual(a, b) {
  const leftIds = Object.keys(a).sort();
  const rightIds = Object.keys(b).sort();
  if (!listEqual(leftIds, rightIds)) return false;
  return leftIds.every((id) => overrideEqual(a[id], b[id]));
}

function specialDaysEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((day, index) => {
    const other = b[index];
    return (
      day.id === other.id &&
      day.label === other.label &&
      scopeEqual(day.gatewayBotIds, other.gatewayBotIds) &&
      day.date === other.date &&
      day.closed === other.closed &&
      windowsListEqual(day.windows, other.windows) &&
      day.message === other.message
    );
  });
}

function emergenciesEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((emergency, index) => {
    const other = b[index];
    return (
      emergency.id === other.id &&
      emergency.label === other.label &&
      scopeEqual(emergency.gatewayBotIds, other.gatewayBotIds) &&
      emergency.from === other.from &&
      emergency.until === other.until &&
      emergency.mode === other.mode &&
      emergency.message === other.message &&
      emergency.createdBy === other.createdBy &&
      emergency.createdAt === other.createdAt
    );
  });
}

/**
 * Deep equality over the known fields of two settings values (both normalized first).
 * Order-insensitive where the server treats order as irrelevant: the gatewayBotIds
 * scope lists.
 */
export function botServiceHoursEqual(a, b) {
  const left = normalizeBotServiceHours(a);
  const right = normalizeBotServiceHours(b);
  return (
    left.version === right.version &&
    left.enabled === right.enabled &&
    left.defaults.timezone === right.defaults.timezone &&
    weeklyEqual(left.defaults.weekly, right.defaults.weekly) &&
    left.defaults.closeOnPublicHolidays === right.defaults.closeOnPublicHolidays &&
    left.defaults.holidayCountry === right.defaults.holidayCountry &&
    left.defaults.mode === right.defaults.mode &&
    left.defaults.message === right.defaults.message &&
    overrideEqual(left.appChannel, right.appChannel) &&
    numbersEqual(left.numbers, right.numbers) &&
    specialDaysEqual(left.specialDays, right.specialDays) &&
    emergenciesEqual(left.emergencies, right.emergencies) &&
    listEqual(left.bypassPhones, right.bypassPhones)
  );
}

// ---- statuses -------------------------------------------------------------------------------

/**
 * "upcoming" | "active" (today) | "past" for a special day. `nowISO` must already be
 * expressed in the timezone the day is judged in (e.g. luxon's
 * DateTime.now().setZone(tz).toISO()) — only its leading "YYYY-MM-DD" is compared.
 */
export function specialDayStatus(day, nowISO) {
  const date = String(day?.date ?? "").slice(0, 10);
  const today = String(nowISO ?? "").slice(0, 10);
  if (!date || !today) return "upcoming";
  if (date < today) return "past";
  if (date > today) return "upcoming";
  return "active";
}

/**
 * "upcoming" | "active" | "past" for an emergency: active while from <= now < until, and an
 * open-ended one (until null) that has started stays active forever.
 */
export function emergencyStatus(emergency, nowISO) {
  const now = Date.parse(String(nowISO ?? ""));
  const from = Date.parse(String(emergency?.from ?? ""));
  if (!Number.isFinite(now) || !Number.isFinite(from)) return "upcoming";
  if (now < from) return "upcoming";
  if (emergency?.until == null) return "active";
  const until = Date.parse(String(emergency.until));
  if (!Number.isFinite(until)) return "active";
  return now < until ? "active" : "past";
}

// ---- display + editing helpers --------------------------------------------------------------

/**
 * Compact one-line rendering of a weekly map, grouping consecutive days with identical
 * hours: "Mon–Fri 08:30–17:30 · Sat 08:30–12:30". Closed days are omitted; an all-closed
 * week reads "Closed all week".
 */
export function weeklySummary(weekly) {
  const map = normalizeWeeklyMap(weekly);
  const texts = WEEKDAY_KEYS.map((key) =>
    map[key].map((w) => `${canonicalHHMM(w.start)}–${canonicalHHMM(w.end)}`).join(" & "),
  );
  const segments = [];
  let index = 0;
  while (index < WEEKDAY_KEYS.length) {
    let end = index;
    while (end + 1 < WEEKDAY_KEYS.length && texts[end + 1] === texts[index]) end += 1;
    if (texts[index]) {
      const label =
        end === index
          ? WEEKDAY_LABELS[WEEKDAY_KEYS[index]]
          : `${WEEKDAY_LABELS[WEEKDAY_KEYS[index]]}–${WEEKDAY_LABELS[WEEKDAY_KEYS[end]]}`;
      segments.push(`${label} ${texts[index]}`);
    }
    index = end + 1;
  }
  return segments.length > 0 ? segments.join(" · ") : "Closed all week";
}

/** New weekly map with `fromKey`'s windows deep-copied onto every key in `toKeys`. */
export function copyDayToDays(weekly, fromKey, toKeys) {
  const map = normalizeWeeklyMap(weekly);
  const source = map[fromKey];
  if (!source) return map;
  for (const key of Array.isArray(toKeys) ? toKeys : []) {
    if (!WEEKDAY_KEYS.includes(key) || key === fromKey) continue;
    map[key] = source.map((window) => ({ ...window }));
  }
  return map;
}

/** Locally generated 24-hex id for new special days / emergencies (kept by the server). */
export function generateLocalId() {
  let out = "";
  for (let i = 0; i < 24; i++) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

/**
 * New settings with one number's override row merged with `patch`. A row whose every field
 * is null is dropped entirely (= inherits the defaults) — the server drops such rows on
 * write anyway, so the draft never grows a meaningless entry.
 */
export function setNumberOverride(settings, botId, patch) {
  const next = normalizeBotServiceHours(settings);
  const id = String(botId ?? "").trim();
  if (!id) return next;
  const current = next.numbers[id] || normalizeOverride(null);
  const row = normalizeOverride({
    ...current,
    ...(patch && typeof patch === "object" ? patch : {}),
  });
  const numbers = { ...next.numbers };
  const hasOverride =
    row.enabled !== null ||
    row.timezone !== null ||
    row.weekly !== null ||
    row.closeOnPublicHolidays !== null ||
    row.mode !== null ||
    row.message !== null;
  if (!hasOverride) delete numbers[id];
  else numbers[id] = row;
  return { ...next, numbers };
}

/** New settings with the special day added or (by id) replaced. Missing ids are generated. */
export function upsertSpecialDay(settings, day) {
  const next = normalizeBotServiceHours(settings);
  const entry = normalizeSpecialDay(day);
  if (!entry.id) entry.id = generateLocalId();
  const specialDays = [...next.specialDays];
  const index = specialDays.findIndex((existing) => existing.id === entry.id);
  if (index >= 0) specialDays[index] = entry;
  else specialDays.push(entry);
  return { ...next, specialDays };
}

/** New settings without the special day. */
export function removeSpecialDay(settings, id) {
  const next = normalizeBotServiceHours(settings);
  const wanted = String(id ?? "").trim();
  return { ...next, specialDays: next.specialDays.filter((day) => day.id !== wanted) };
}

/** New settings with the emergency added or (by id) replaced. Missing ids are generated. */
export function upsertEmergency(settings, emergency) {
  const next = normalizeBotServiceHours(settings);
  const entry = normalizeEmergency(emergency);
  if (!entry.id) entry.id = generateLocalId();
  const emergencies = [...next.emergencies];
  const index = emergencies.findIndex((existing) => existing.id === entry.id);
  if (index >= 0) emergencies[index] = entry;
  else emergencies.push(entry);
  return { ...next, emergencies };
}

/** New settings without the emergency. */
export function removeEmergency(settings, id) {
  const next = normalizeBotServiceHours(settings);
  const wanted = String(id ?? "").trim();
  return { ...next, emergencies: next.emergencies.filter((e) => e.id !== wanted) };
}

/**
 * End an ACTIVE emergency at `nowISO` (sets until = now). Anything not currently active is
 * returned unchanged — an upcoming one is edited or deleted instead, a past one is over.
 * When "now" coincides with the start instant, the end is nudged a minute later so the
 * stored range still satisfies from < until.
 */
export function endEmergencyNow(settings, id, nowISO) {
  const next = normalizeBotServiceHours(settings);
  const wanted = String(id ?? "").trim();
  const target = next.emergencies.find((e) => e.id === wanted);
  if (!target || emergencyStatus(target, nowISO) !== "active") return next;
  const from = Date.parse(target.from);
  const now = Date.parse(String(nowISO ?? ""));
  const untilMs = Number.isFinite(from) && now <= from ? from + 60_000 : now;
  const until = new Date(untilMs).toISOString();
  return {
    ...next,
    emergencies: next.emergencies.map((e) => (e.id === wanted ? { ...e, until } : e)),
  };
}
